import { isSandbox } from "@/config/app-config";
import Cookies from "js-cookie";
import { cookieNames } from "./cookie.config";

export type CookieName = (typeof cookieNames)[number];

const SANDBOX_COOKIE_PREFIX = "sandbox_";

/**
 * Maps the cookie `name` used in app code to the name stored in the browser.
 * In sandbox, names are prefixed with `sandbox_` so prod and sandbox cookies do not clash.
 */
export function resolveCookieName(name: CookieName): string {
  if (isSandbox()) return `${SANDBOX_COOKIE_PREFIX}${name}`;
  return name;
}

function getCookieDomain(): string | undefined {
  const hostname = window.location.hostname;
  if (hostname === "clicknpark.com" || hostname.endsWith(".clicknpark.com")) return ".clicknpark.com";
  return undefined;
}

/** Default attributes for all app cookies (domain, secure, sameSite, path, expires). */
export function getDefaultCookieAttributes(): Cookies.CookieAttributes {
  return {
    path: "/",
    expires: 365,
    sameSite: "lax",
    secure: window.location.protocol === "https:",
    domain: getCookieDomain(),
  };
}

function mergeAttributes(overrides?: Cookies.CookieAttributes): Cookies.CookieAttributes {
  return { ...getDefaultCookieAttributes(), ...overrides };
}

/**
 * Raw cookie string as stored in the browser (no JSON parsing).
 * Use {@link getCookie} when you need parsed JSON or app-level typing.
 */
export function getCookieString(name: CookieName): string | undefined {
  const value = Cookies.get(resolveCookieName(name));
  return value === undefined ? undefined : value;
}

/**
 * Read one cookie value (js-cookie wrapper).
 * JSON values are parsed; plain strings are returned as-is.
 */
export function getCookie<R>(name: CookieName): R | undefined {
  const cookie = getCookieString(name);
  if (!cookie) return undefined;
  try {
    return JSON.parse(cookie) as R;
  } catch {
    return cookie as unknown as R;
  }
}

/**
 * Low-level set (no React store notification). Prefer {@link setCookie} from `@/features/cookies/cookie.store` in app code.
 */
export function setCookieValue<T = object>(name: CookieName, value: T, overrides?: Cookies.CookieAttributes): void {
  if (typeof value === "string") {
    Cookies.set(resolveCookieName(name), value, mergeAttributes(overrides));
    return;
  } else {
    Cookies.set(resolveCookieName(name), JSON.stringify(value), mergeAttributes(overrides));
  }
}

/**
 * Low-level remove (no React store notification). Prefer {@link removeCookie} from `@/features/cookies/cookie.store` in app code.
 */
export function removeCookieValue<T = object>(name: CookieName, overrides?: Cookies.CookieAttributes): void {
  Cookies.remove(resolveCookieName(name), mergeAttributes(overrides));
}
